/**
 * Every user-facing English string. Keys follow the iOS convention `feature.section.element`;
 * `{name}` placeholders are filled by `t()`.
 */

export const en = {
  'app.name': 'Map My Money',
  'app.tagline': 'Your financial GPS',
  'app.skipToContent': 'Skip to content',
  'app.privateAlpha': 'Private alpha',
  'app.docTitle': '{page} · Map My Money',
  'docTitle.share': 'Share',
  'docTitle.confirmShare': 'Confirm a share',
  'docTitle.signIn': 'Sign in',

  'nav.main': 'Main',
  'nav.primary': 'Primary',
  'nav.home': 'Home',
  'nav.forecast': 'Forecast',
  'nav.plan': 'Plan',
  'nav.whatIf': 'What If',
  'nav.goals': 'Goals',
  'nav.connections': 'Connections',
  'nav.settings': 'Settings',
  'nav.plans': 'Plans',

  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.saveChanges': 'Save changes',
  'common.delete': 'Delete',
  'common.close': 'Close',
  'common.done': 'Done',
  'common.undo': 'Undo',
  'common.restore': 'Restore',
  'common.retry': 'Try again',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.manage': 'Manage',
  'common.history': 'History',
  'common.add': 'Add',
  'common.reset': 'Reset',
  'common.none': '—',
  'common.loading': 'Loading your plan…',
  'common.today': 'Today',
  'common.yesterday': 'Yesterday',
  'common.justNow': 'just now',
  'common.minutesAgo': '{n} min ago',
  'common.hoursAgo': '{n}h ago',
  'common.daysAgo': '{n}d ago',
  'common.saving': 'Saving…',
  'common.saved': 'Saved',
  'common.signOut': 'Sign out',
  'common.noChange': 'No change',
  'common.notReached': 'Not in view',
  'common.years': '{n}Y',
  'common.perMonth': '/mo',
  'common.perYear': '/yr',

  'time.today': 'Today, {time}',
  'time.yesterday': 'Yesterday, {time}',

  'greeting.morning': 'Good morning, {name}',
  'greeting.afternoon': 'Good afternoon, {name}',
  'greeting.evening': 'Good evening, {name}',
  'greeting.morningPlain': 'Good morning',
  'greeting.afternoonPlain': 'Good afternoon',
  'greeting.eveningPlain': 'Good evening',

  'sync.source.web': 'Web',
  'sync.source.ios': 'iPhone',
  'sync.source.mcp': '{client}',
  'sync.status': 'Updated from {source} · {ago}',
  'sync.saving': 'Saving your change…',

  'home.title': 'Map',
  'home.netWorth': 'Net worth',
  'home.restOfMonth': 'Rest of this month',
  'home.restOfMonth.detail': 'Cash {cash} · investments {investments} · {end} on {date}',
  'home.anchor.upToDate': 'Up to date',
  'home.anchor.checkIn': 'Based on your check-in on {date}',
  'home.anchor.checkInAction': 'check in to sharpen this',
  'home.anchor.estimate': 'Estimated from what you entered on {date}',
  'home.anchor.estimateAction': 'do your first check-in to make this exact',
  'home.thisMonth.cashOn': 'Cash on {date}',
  'home.thisMonth.startOn': 'Start · {date}',
  'home.projectedIn': 'Projected in {years} years',
  'home.fromNow': 'from {amount}',
  'home.thisMonth': 'This month',
  'home.thisMonth.chart': 'This month’s cash: {start} on {startDate}, {today} today, projected {end} by {endDate}.',
  'home.thisMonth.today': 'Today',
  'home.thisMonth.start': 'Start',
  'home.thisMonth.monthEnd': 'Month end',
  'home.thisMonth.now': 'Now',
  'home.thisMonth.confirmed': 'Confirmed',
  'home.thisMonth.projected': 'Projected',
  'home.thisMonth.upcoming': 'Still to come',
  'home.thisMonth.soFar': 'So far this month',
  'home.thisMonth.nothingDue': 'Nothing else due this month',
  'home.waypoints': 'Check-ins',
  'home.waypoints.count': '{n} check-ins',
  'home.logWaypoint': 'Check in',
  'home.logWaypoint.since': '{span} since your last check-in',
  'home.logWaypoint.first': 'Do your first check-in',
  'home.logWaypoint.changes': '{span} of changes since {date}',
  'home.logWaypoint.daysBadge': '{n} days since your last check-in',
  'home.northStar': 'Your North Star',
  'home.northStar.none': 'Set a goal to see your North Star here.',
  'home.whatYouOwn': 'What you own',
  'home.whatYouOwn.addAsset': 'Add asset',
  'home.whatYouOwn.sinceBought': '{pct} since bought',
  'home.whatYouOwn.none': 'Nothing here yet.',
  'home.recentChanges': 'Recent changes',
  'home.nextGoals': 'Next goals',
  'home.breakdown.cash': 'Cash',
  'home.breakdown.investments': 'Investments',
  'home.breakdown.property': 'Property equity',
  'home.breakdown.assets': 'Vehicles & other',
  'home.breakdown.debt': 'Debt',
  'home.thisMonth.range': '{start} – {end}',
  'home.thisMonth.nextUp': 'Next up',
  'home.logWaypoint.today': 'Checked in today · you’re up to date',
  'home.recentChanges.none': 'Changes to your plan will appear here.',
  'home.waypointLogged': 'Checked in · {delta}',
  'home.quickAction': 'Quick calculator',
  'home.forecast': 'Net worth and forecast',

  'waypoint.dialog.title': 'Check in',
  'waypoint.step.balances': 'Your balances',
  'waypoint.step.done': 'Done',
  'waypoint.step.announce': 'Step {n} of {total}, {name}',
  'waypoint.steps': 'Check-in steps',
  'waypoint.heading.today': 'Your balances today',
  'waypoint.heading.on': 'Your balances on {date}',
  'waypoint.intro': 'Filled in from your map.',
  'waypoint.intro.withFlows': 'Filled in from your map and your changes.',
  'waypoint.allMatch': 'All my balances match',
  'waypoint.allMatch.label': 'All my balances match, finish check-in',
  'waypoint.date': 'Date of these balances',
  'waypoint.date.change': 'Change date',
  'waypoint.date.today': 'Use today',
  'waypoint.dateHint': 'Today or an earlier day. The expected numbers follow the date.',
  'waypoint.cashHint': 'Current accounts and savings',
  'waypoint.investmentsHint': 'ISAs, pensions, shares and funds',
  'waypoint.debts': 'What you owe',
  'waypoint.debtsHint': 'From your latest statements',
  'waypoint.debtNote': 'Your home and car are valued from your map.',
  'waypoint.amountLabel': '{field}, in {currency}',
  'waypoint.expected': 'Expected {amount}',
  'waypoint.matches': 'Matches',
  'waypoint.matches.label': '{field} matches {amount}',
  'waypoint.typed': 'You typed this',
  'waypoint.diff.less': '{amount} less than expected',
  'waypoint.diff.more': '{amount} more than expected',
  'waypoint.diff.same': 'As expected',
  'waypoint.suggest.cost': 'Is there a regular cost that isn’t on your map?',
  'waypoint.suggest.income': 'Is there regular income that isn’t on your map?',
  'waypoint.suggest.add': 'Add it',
  'waypoint.checked.all': 'All {n} checked',
  'waypoint.checked.left': '{n} not checked yet, saved as estimates',
  'waypoint.checked.left.one': '1 not checked yet, saved as an estimate',
  'waypoint.back': 'Back',
  'waypoint.save': 'Save check-in',
  'waypoint.shortcut': '{keys} to save',
  'waypoint.refused': 'We couldn’t save this check-in',
  'waypoint.done.title': 'Checked in',
  'waypoint.done.netWorth': 'Net worth {amount} on {date}.',
  'waypoint.done.lower': '{amount} lower than expected',
  'waypoint.done.higher': '{amount} higher than expected',
  'waypoint.done.same': 'Exactly as expected',
  'waypoint.done.expected': 'Expected',
  'waypoint.done.actual': 'Actual',
  'waypoint.done.why': 'Where the difference is',
  'waypoint.done.balance.lower': '{label} {amount} lower',
  'waypoint.done.balance.higher': '{label} {amount} higher',
  'waypoint.done.balance.same': '{label} as expected',
  'waypoint.done.goals': 'Your goals',
  'waypoint.done.goal.earlier': '{span} earlier',
  'waypoint.done.goal.later': '{span} later',
  'waypoint.done.goal.still': 'Still {date}',
  'waypoint.done.goal.reachable': 'Now within reach',
  'waypoint.done.goal.unreachable': 'No longer within your forecast',
  'waypoint.done.goal.reached': 'Reached',
  'waypoint.done.allChecked': 'Every number checked.',
  'waypoint.done.next': 'Next check-in: around {date}',
  'waypoint.done.forecast': 'See your forecast',
  'waypoint.field.cash': 'cash',
  'waypoint.field.investments': 'investments',
  'waypoint.estimate.all': 'You didn’t change any numbers, so this check-in is kept as an estimate.',
  'waypoint.estimate.one': 'You didn’t change {fields}, so we’ve kept it as an estimate.',
  'waypoint.estimate.many': 'You didn’t change {fields}, so we’ve kept them as estimates.',
  'waypoint.estimate.join': '{first} and {last}',
  'waypoint.estimate.hint.cash': 'Check your banking app when you can.',
  'waypoint.estimate.hint.investments': 'Check your ISA app when you can.',
  'waypoint.estimate.hint.debt': 'Check your latest statement when you can.',
  'waypoint.step.happened': 'What happened',
  'waypoint.step.happened.short': 'Happened',
  'waypoint.step.balances.short': 'Balances',
  'waypoint.heading.first': 'What do you have today?',
  'waypoint.done.cause.added': '{label} you added',
  'waypoint.done.cause.more': '{label} was {amount} more',
  'waypoint.done.cause.less': '{label} was {amount} less',
  'waypoint.done.cause.skipped': '{label} on {date} didn’t happen',
  'waypoint.done.cause.markets': 'Markets',
  'waypoint.done.cause.debt': '{label} balance',
  'waypoint.done.cause.notOnMap.out': 'Spending that isn’t on your map',
  'waypoint.done.cause.notOnMap.in': 'Money in that isn’t on your map',
  'waypoint.done.cause.startingEstimate': 'Difference from your starting estimate',
  'waypoint.done.cause.none': 'Everything went as your map expected',
  'waypoint.done.byBalance': 'You skipped what happened, so the difference is split by balance.',
  'waypoint.done.mapChanged': 'Saved to your map: {changes}.',
  'waypoint.done.mapChange': '{label} {amount} from {date}',
  'waypoint.done.mapChange.monthly': '{label} {amount} a month from {date}',
  'waypoint.done.move.title': 'Your biggest move',
  'waypoint.done.move.try': 'Try it in What If',
  'waypoint.done.move.tryLabel': 'Try it in What If: {cost}',
  'move.per.weekly': 'a week',
  'move.per.fortnightly': 'a fortnight',
  'move.per.monthly': 'a month',
  'move.per.quarterly': 'a quarter',
  'move.per.yearly': 'a year',
  'move.cost.cut': '{amount} {per} less on {item}, into {to}',
  'move.cost.cut.cash': '{amount} {per} less on {item}, kept as cash',
  'move.cost.redirect': 'When {item} ends, keep paying its {amount} {per} into {to} from {month}',
  'move.cost.pension': '{percent}% more of your pay into {item}: {amount} {per}',
  'move.cost.overpay': '{amount} {per} more on {item}',
  'move.cost.then': '{cost}, then {then} once it is paid off',
  'move.effect.reachable': 'Within reach: {month}',
  'move.effect.sooner.one': '1 month sooner, {month}',
  'move.effect.sooner': '{n} months sooner, {month}',
  'move.effect.weeks': 'About {span} sooner',
  'move.effect.closer': '{amount} closer by {month}',
  'move.effect.more': '{amount} more by {month}',
  'move.effect.onTrack': 'On track for {month}',
  'flows.title.since': 'What happened since {date}',
  'flows.title.setup': 'Since you set up your map on {date}',
  'flows.title.first': 'Your first check-in',
  'flows.lead': 'Your map expected {n} payments. Change anything that was different. Everything else counts as expected.',
  'flows.lead.one': 'Your map expected 1 payment. Change it if it was different. Otherwise it counts as expected.',
  'flows.lead.first': 'Your map is an estimate until you check in. Start with what happened.',
  'flows.lead.long': 'It’s been {span}. Change what you remember, or go straight to your balances.',
  'flows.caughtUp.title': 'All caught up',
  'flows.caughtUp.since': 'Nothing was due since your check-in on {date}.',
  'flows.caughtUp.setup': 'Nothing was due since you set up your map on {date}.',
  'flows.chips': 'Groups',
  'flows.chip.in': 'In',
  'flows.chip.invested': 'Invested',
  'flows.chip.spent': 'Spent',
  'flows.chip.debts': 'Debts',
  'flows.chip.label': '{group}: {n} payments, {amount}',
  'flows.group.in': 'Money in',
  'flows.group.invested': 'Investments',
  'flows.group.spent': 'Spending',
  'flows.group.debts': 'Debt payments',
  'flows.group.count': '{n}',
  'flows.group.countOf': '{happened} of {n}',
  'flows.item.one': '1 payment · {date}',
  'flows.item.few': '{n} payments · {dates}',
  'flows.item.many': '{n} payments · {first} to {last}',
  'flows.item.oneOff': 'One-off · {date}',
  'flows.item.times': '{n} × {amount}',
  'flows.item.count': '{n} payments',
  'flows.item.changed': '{n} changed',
  'flows.item.skipped': '{n} skipped',
  'flows.item.didntHappen': '{n} didn’t happen',
  'flows.item.label': '{label}, {payments}, {amount}, {status}',
  'flows.status.expected': 'As expected',
  'flows.status.more': '{amount} more',
  'flows.status.less': '{amount} less',
  'flows.status.skipped': 'Didn’t happen',
  'flows.status.added': 'Added',
  'flows.status.monthly': 'Added · every month',
  'flows.action.change': 'Change amount',
  'flows.action.change.label': 'Change amount, {label}, {date}',
  'flows.action.skip': 'Didn’t happen',
  'flows.action.skip.label': 'Didn’t happen, {label}, {date}',
  'flows.action.undo.label': 'Undo, {label}, {date}',
  'flows.action.remove.label': 'Remove {label}',
  'flows.amount.label': 'What was paid for {label} on {date}',
  'flows.amount.hint': 'Enter to save, Escape to cancel',
  'flows.latest.label': '{label}, {date}: amount paid',
  'flows.amount.invalid': 'Type an amount of 0 or more, or say it didn’t happen.',
  'flows.debt.split': '{principal} off the balance, {interest} interest',
  'flows.pension.split': '{own} + {employer} from your employer',
  'flows.pension.relief': '{own} + {relief} tax relief',
  'flows.pension.both': '{own} + {employer} from your employer + {relief} tax relief',
  'flows.fromNowOn': '{label} is {amount} from now on',
  'flows.fromNowOn.hint': 'updates your map from {date}',
  'flows.add': 'Add something that isn’t on your map',
  'flows.add.caughtUp': 'Something happened that isn’t on your map',
  'flows.add.title': 'Something that isn’t on your map',
  'flows.add.label': 'What was it?',
  'flows.add.placeholder': 'Car repair',
  'flows.add.amount': 'Amount',
  'flows.add.date': 'When',
  'flows.add.direction': 'Money in or out',
  'flows.add.in': 'Money in',
  'flows.add.out': 'Money out',
  'flows.add.save': 'Add it',
  'flows.add.needsLabel': 'Say what it was.',
  'flows.add.needsAmount': 'Add an amount above 0.',
  'flows.add.needsDate': 'Pick a day from {from} to {to}.',
  'flows.monthly.ask': 'Happens every month?',
  'flows.monthly.action': 'Add it to your map',
  'flows.monthly.on': 'Joins your map monthly from {date} when you save',
  'flows.small': '{n} smaller items',
  'flows.small.hint': 'Under {amount} each · {total}',
  'flows.small.show': 'Show',
  'flows.changes.title': 'Your changes',
  'flows.changes.none': 'Nothing changed. If it all went as your map expected, carry on.',
  'flows.changes.row': '{label}, {date}',
  'flows.changes.skipped': '{label}, {date} skipped',
  'flows.changes.kept': '{amount} kept',
  'flows.changes.missed': '{amount} not received',
  'flows.changes.cash': 'So your cash should be about {amount}. The map alone said {map}.',
  'flows.changes.short.one': '1 change · cash should be about {amount}',
  'flows.changes.short': '{n} changes · cash should be about {amount}',
  'flows.next.all': 'All as expected',
  'flows.next': 'Next: your balances',
  'flows.skip': 'Skip to balances',
  'flows.skip.long': 'Just my balances',
  'flows.skip.short': 'Skip',
  'flows.skip.long.short': 'Just my balances',
  'flows.announce.changed': '{label}, {date}: {amount}, {diff}',
  'flows.announce.skipped': '{label}, {date}: didn’t happen',
  'flows.announce.undone': '{label}, {date}: as expected',
  'flows.announce.added': '{label} added',
  'flows.announce.removed': '{label} removed',
  'flows.shortcut': '{keys} to continue',

  'line.now': 'Now · {date}',
  'line.endOf': 'End of {month}',

  'forecast.title': 'Forecast',
  'forecast.milestones.label': 'Goals on this chart',
  'forecast.milestones.item': '{label}: {date}',
  'forecast.projectedNetWorth': 'Projected net worth',
  'forecast.change': '{change} ({pct})',
  'forecast.from': 'from {amount}',
  'forecast.timeframe': 'Timeframe',
  'forecast.mode': 'Chart mode',
  'forecast.cumulative': 'Cumulative',
  'forecast.cashflow': 'Cashflow',
  'forecast.series': 'Series',
  'forecast.series.cash': 'Cash',
  'forecast.series.investment': 'Investments',
  'forecast.series.equity': 'Equity',
  'forecast.series.debt': 'Debt',
  'forecast.series.netWorth': 'Net worth',
  'forecast.cashflow.in': 'Money in',
  'forecast.cashflow.out': 'Money out',
  'forecast.cashflow.kept': 'Kept',
  'forecast.cashflow.keptHint': 'stays yours: investing and debt repaid',
  'forecast.cashflow.growth': 'Growth',
  'forecast.cashflow.growthHint': 'returns, employer match, tax relief and value changes',
  'forecast.cashflow.change': 'Net worth change',
  'forecast.cashflow.fromMonth': 'From {month}',
  'forecast.cashflow.note': 'Kept is the part of money out that stays yours: investing and debt repaid. Growth is returns, employer match, tax relief and value changes. In minus out is the change in your cash; add kept and growth for the change in net worth. The first bar counts from {month}.',
  'forecast.cashflow.valuetext': '{year}: in {in}, out {out}, kept {kept}, growth {growth}',
  'forecast.chart.label': 'Net worth forecast, {from} to {to}. Now, {date}: {start}; {end} by the end of {last}.',
  'forecast.startNote': 'Now · {date}: {now}. Each later point is the end of a month.',
  'forecast.key.mortgageFree': 'Mortgage paid off',
  'forecast.key.debtFreeHint': 'Every debt except the mortgage',
  'forecast.cashflowChart.label': 'Yearly money in and out, {from} to {to}.',
  'forecast.tooltip.invested': 'Invested {invested} · Debt {debt}',
  'forecast.goals': 'Goals',
  'forecast.key.title': 'Key numbers',
  'forecast.key.monthlySavings': 'Saving each month',
  'forecast.key.savingsRate': 'Savings rate',
  'forecast.key.passive': 'Passive income at the end',
  'forecast.key.debtFree': 'Debt free',
  'forecast.key.totalIncome': 'Earned over {years} years',
  'forecast.addGoal': 'Add',
  'forecast.whatIf': 'What If',
  'forecast.table.caption': 'Net worth now and at the end of each year',
  'forecast.table.year': 'When',

  'goals.title': 'Goals',
  'goals.eyebrow': 'Where you’re heading',
  'goals.add': 'Add goal',
  'goals.none': 'No goals yet. Add one to see when you’ll get there.',
  'goals.eta': 'On course for {date} · {eta} away',
  'goals.onCourse': 'On course for {date}',
  'goals.achieved': 'Reached this month',
  'goals.beyond': 'Not within {years} years',
  'goals.progress': '{pct}% of the way',
  'goals.form.label': 'Name',
  'goals.form.series': 'Track',
  'goals.form.target': 'Target',
  'goals.form.operation': 'Direction',
  'goals.form.reachAtLeast': 'Reach at least',
  'goals.form.fallTo': 'Fall to',
  'goals.series.netWorth': 'Net worth',
  'goals.series.cash': 'Cash',
  'goals.series.investment': 'Invested',
  'goals.series.debt': 'Debt',
  'goals.series.passive': 'Passive income',
  'goals.edit': 'Edit goal',
  'goals.new': 'New goal',
  'goals.northStar': 'North Star',
  'goals.by.label': 'By',
  'goals.by.hint': 'Optional. We’ll tell you if you’re on track.',
  'goals.by.clear': 'Clear date',
  'goals.by.onTrack': 'On track for {date}',
  'goals.by.onTrackAhead': 'On track for {date} · {span} before your date',
  'goals.by.after': '{span} after your {by} date · {date}',
  'goals.by.short': '{amount} short by {by}',

  'plan.eyebrow': 'Everything in your plan',
  'plan.title': 'Your plan',
  'plan.add': 'Add',
  'plan.filter': 'Filter plan items',
  'plan.filter.all': 'All',
  'plan.filter.income': 'Income',
  'plan.filter.spending': 'Spending',
  'plan.filter.investing': 'Investing',
  'plan.filter.debts': 'Debts',
  'plan.filter.assets': 'Property & assets',
  'plan.items': 'Plan items',
  'plan.col.name': 'Name',
  'plan.col.type': 'Type',
  'plan.col.amount': 'Amount',
  'plan.col.frequency': 'Frequency',
  'plan.col.starts': 'Starts',
  'plan.col.ends': 'Ends',
  'plan.col.grows': 'Grows',
  'plan.col.status': 'Active',
  'plan.type.income': 'Income',
  'plan.type.expense': 'Expense',
  'plan.type.investment': 'Investing',
  'plan.type.debt': 'Debt',
  'plan.type.property': 'Property',
  'plan.type.vehicle': 'Vehicle',
  'plan.type.asset': 'Asset',
  'plan.freq.oneTime': 'Once',
  'plan.freq.daily': 'Daily',
  'plan.freq.weekly': 'Weekly',
  'plan.freq.fortnightly': 'Fortnightly',
  'plan.freq.monthly': 'Monthly',
  'plan.freq.quarterly': 'Quarterly',
  'plan.freq.yearly': 'Yearly',
  'plan.freq.custom': 'Custom',
  'plan.freq.value': 'Value',
  'plan.grows.pct': '{pct}%/yr',
  'plan.grows.match': '+{pct}% match',
  'plan.grows.depreciating': 'Depreciating',
  'plan.active': '{name} active',
  'plan.summary': 'Monthly',
  'plan.summary.in': 'In {amount}',
  'plan.summary.out': 'Out {amount}',
  'plan.summary.invested': 'Invested {amount}',
  'plan.summary.from': 'From {month}',
  'plan.summary.laterIn': 'in +{amount}',
  'plan.summary.laterOut': 'out +{amount}',
  'plan.summary.laterInvested': 'invested +{amount}',
  'plan.planned': 'Planned for {month}',
  'plan.detail': '{name} detail',
  'plan.detail.close': 'Close detail panel',
  'plan.detail.empty': 'Select an item to edit it.',
  'plan.field.name': 'Name',
  'plan.field.amount': 'Amount',
  'plan.field.payment': 'Payment',
  'plan.field.frequency': 'Frequency',
  'plan.field.starts': 'Starts',
  'plan.field.ends': 'Ends',
  'plan.field.grows': 'Grows (%/yr)',
  'plan.field.rate': 'Rate (%)',
  'plan.field.return': 'Return (%/yr)',
  'plan.field.balance': 'Balance today',
  'plan.field.startBalance': 'Starting balance',
  'plan.field.employerMatch': 'Employer match (% of their pay)',
  'plan.field.principal': 'Original loan',
  'plan.field.term': 'Term (months)',
  'plan.field.linked': 'Linked to',
  'plan.field.purchasePrice': 'Purchase price',
  'plan.field.purchaseDate': 'Bought',
  'plan.field.value': 'Value today',
  'plan.field.plannedFor': 'Planned for',
  'plan.field.balanceAmount': 'Balance',
  'plan.field.balanceOn': 'On',
  'plan.field.notFuture': 'Can’t be after today',
  'plan.field.atMost100': 'Must be 100 or less',
  'plan.field.matchIncome': 'Whose pay?',
  'plan.match.largest': 'The largest income ({name})',
  'plan.match.largestNone': 'The largest income',
  'plan.match.terms': 'You {own}%, employer {match}% of {income}',
  'plan.match.termsMatchOnly': 'Employer {match}% of {income}',
  'plan.match.noIncome': 'Add an income for the match to follow.',
  'plan.field.taxAccount': 'Account type',
  'plan.field.taxRelief': 'Tax relief (%)',
  'plan.tax.none': 'No tax relief',
  'plan.tax.default': 'Default for this account: {pct}%',
  'plan.tax.isa_stocks': 'Stocks & shares ISA',
  'plan.tax.isa_cash': 'Cash ISA',
  'plan.tax.sipp': 'SIPP',
  'plan.tax.workplace_pension': 'Workplace pension',
  'plan.tax.401k_traditional': '401(k)',
  'plan.tax.401k_roth': 'Roth 401(k)',
  'plan.tax.ira_traditional': 'Traditional IRA',
  'plan.tax.ira_roth': 'Roth IRA',
  'plan.tax.hsa': 'HSA',
  'plan.tax.brokerage': 'General investment account',
  'plan.rates.title': 'Planned rate changes',
  'plan.rates.none': 'No planned rate changes.',
  'plan.rates.date': 'From',
  'plan.rates.rate': 'New rate (%)',
  'plan.rates.add': 'Add a rate change',
  'plan.rates.remove': 'Remove rate change {n}',
  'plan.rates.afterStart': 'Must be after the debt starts',
  'plan.rates.duplicate': 'Already has a rate on this date',
  'plan.rates.base': '{rate}%',
  'plan.rates.from': '{rate}% from {month}',
  'plan.rates.then': '{rates}, then {next}',
  'plan.rates.paymentRises': 'payment rises {amount} a month from {month}',
  'plan.rates.paymentFalls': 'payment falls {amount} a month from {month}',
  'plan.rates.paymentStays': 'payment stays {amount}, paid off {date}',
  'plan.rates.paymentStaysNever': 'payment stays {amount}, not paid off within 50 years',
  'plan.field.appreciation': 'Grows (%/yr)',
  'plan.field.active': 'Include in forecast',
  'plan.field.payoff': 'Payoff',
  'plan.field.payoffChart': '{name} balance falling from {from} today to £0 in {date}',
  'plan.field.payoffNever': 'Not paid off within 50 years at this payment',
  'plan.field.required': 'Enter a value',
  'plan.field.number': 'Enter a number',
  'plan.field.positive': 'Must be zero or more',
  'plan.field.endAfterStart': 'Must be after the start date',
  'plan.linkedNote': 'Selling the {asset} pays this off automatically.',
  'plan.new': 'New {type}',
  'plan.newItem.income': 'Income',
  'plan.newItem.expense': 'Expense',
  'plan.newItem.investment': 'Investment',
  'plan.newItem.debt': 'Debt',
  'plan.addMenu': 'Add to plan',
  'plan.empty': 'Nothing in this group yet.',
  'plan.deleteConfirm': 'Remove {name} from your plan?',
  'plan.summaryText.changed': '{name} {field} {from} → {to}',
  'plan.summaryText.amount': '{name} → {amount}',
  'plan.summaryText.updated': 'Updated {name}',
  'plan.summaryText.added': 'Added ‘{name}’ {amount}',
  'plan.summaryText.removed': 'Removed ‘{name}’',
  'plan.summaryText.paused': 'Paused {name}',
  'plan.summaryText.resumed': 'Resumed {name}',
  'plan.summaryText.settings': 'Updated plan settings',
  'plan.summaryText.goalAdded': 'Added goal ‘{name}’',
  'plan.summaryText.goalUpdated': 'Updated goal ‘{name}’',
  'plan.summaryText.goalRemoved': 'Removed goal ‘{name}’',
  'plan.summaryText.whatIf': 'Applied what-if: {changes}',
  'plan.summaryText.started': 'Started a plan',
  'plan.summaryText.balance': '{name} balance {amount} on {date}',
  'plan.summaryText.balanceUndated': '{name} balance {amount}',
  'plan.summaryText.rates': '{name} rates: {rates}',
  'plan.summaryText.ratesCleared': '{name} rate changes removed',
  'plan.summaryText.matchIncome': '{name} match follows {income}',
  'plan.summaryText.matchLargest': '{name} match follows the largest income',
  'plan.summaryText.taxRelief': '{name} tax relief: {account}',
  'plan.summaryText.taxReliefPct': '{name} tax relief: {account}, {pct}%',
  'plan.summaryText.taxReliefCleared': '{name} tax relief removed',
  'plan.from.label': 'From',
  'plan.from.today': 'from today, {date}',
  'plan.from.on': 'from {date}',
  'plan.from.changeDate': 'Change date',
  'plan.from.start': 'From the start',
  'plan.from.backToToday': 'From today',
  'plan.from.backToChange': 'From the planned change',
  'plan.from.always': 'From the start: it was always {values}',
  'plan.from.alwaysHint': 'A correction: months already past change too.',
  'plan.from.afterChange': 'On or after {date}, when the planned change starts. Remove that change to edit earlier days.',
  'plan.from.change.amount': '{from} → {to}',
  'plan.from.change.annualIncreasePercent': 'yearly rise {from}% → {to}%',
  'plan.from.change.interestRate': 'growth {from}% → {to}%',
  'plan.from.change.employerMatch': 'match {from}% → {to}%',
  'plan.from.value.annualIncreasePercent': 'rising {pct}% a year',
  'plan.from.value.interestRate': 'growing {pct}% a year',
  'plan.from.value.employerMatch': 'a {pct}% match',
  'plan.from.summaryOn': '{summary} from {date}',
  'plan.from.summaryStart': '{summary} from the start',
  'plan.from.next': '→ {amount} from {month}',
  'plan.from.nextOther': 'Changes from {month}',
  'plan.history.title': 'Over time',
  'plan.history.until': '{values} until {date}',
  'plan.history.between': '{values} from {from} to {until}',
  'plan.history.since': '{values} since {date}',
  'plan.history.from': '{values} from {date}',
  'plan.history.now': 'Now',
  'plan.history.remove': 'Remove',
  'plan.history.removeLabel': 'Remove the change from {date}',
  'plan.history.removedSummary': '{name}: removed the change from {date}',
  'notice.rates.title': 'Planned rate changes now count',
  'notice.rates.body': 'Payoff dates for {debts} may have moved.',
  'notice.rates.dismiss': 'Dismiss',

  'whatIf.eyebrow': 'What if studio',
  'whatIf.move.eyebrow': 'Your biggest move',
  'whatIf.move.changes': 'The move',
  'whatIf.title': 'What if…',
  'whatIf.subtitle': 'Try changes without touching your plan',
  'whatIf.quick.title': 'Quick Calculator',
  'whatIf.quick.subtitle': 'Slide to explore possibilities',
  'whatIf.saveScenario': 'Save scenario',
  'whatIf.apply': 'Apply to my plan',
  'whatIf.makeReal': 'Make this real',
  'whatIf.share': 'Share',
  'whatIf.changes': 'Changes',
  'whatIf.addChange': 'Add change',
  'whatIf.removeChange': 'Remove {name}',
  'whatIf.noChanges': 'Add a change to see how it moves your future.',
  'whatIf.current': 'Current plan',
  'whatIf.withChanges': 'With changes',
  'whatIf.currentShort': 'Current',
  'whatIf.extraPerMonth': 'Extra per month',
  'whatIf.netWorthIn': 'Net worth in {years}Y',
  'whatIf.worthIn': 'Worth in {years}yr',
  'whatIf.betterOffBy': 'Better off by',
  'whatIf.difference': 'Difference',
  'whatIf.goalImpact': 'Goal impact',
  'whatIf.faster': '{span} faster',
  'whatIf.slower': '{span} later',
  'whatIf.nowReachable': 'Now reachable',
  'whatIf.compare': 'Compare scenarios',
  'whatIf.compare.none': 'Saved scenarios appear here so you can compare them.',
  'whatIf.compare.netWorth': '{years}y net worth',
  'whatIf.compare.load': 'Load',
  'whatIf.compare.remove': 'Remove scenario {name}',
  'whatIf.scenarioName': 'Scenario name',
  'whatIf.scenarioSaved': 'Scenario saved',
  'whatIf.applied': 'Applied to your plan',
  'whatIf.chart.label': 'Net worth over {years} years: current plan reaches {baseline}, with changes {modified}.',
  'whatIf.claudeHint': 'Asking Claude “what if I invest £300 more?” works too',
  'whatIf.slider.income': 'Income',
  'whatIf.slider.expenses': 'Expenses',
  'whatIf.slider.expensesHint': 'Spend less',
  'whatIf.slider.investing': 'Investing',
  'whatIf.slider.incomeAria': 'Extra income per month',
  'whatIf.slider.expensesAria': 'Reduce monthly spending',
  'whatIf.slider.investingAria': 'Extra investing per month',
  'whatIf.kind.raise': 'Salary rise',
  'whatIf.kind.raiseFrom': 'Salary rise from {date}',
  'whatIf.kind.side': 'Side income',
  'whatIf.kind.spendLess': 'Spend less each month',
  'whatIf.kind.invest': 'Invest more each month',
  'whatIf.kind.windfall': 'Windfall',
  'whatIf.kind.child': 'Have a child',
  'whatIf.kind.car': 'New car',
  'whatIf.kind.carLoan': 'New car loan',
  'whatIf.kind.spendingCut': 'Spending cut',
  'whatIf.step.redirectAdd': 'When a debt ends',
  'whatIf.step.percentAdd': 'Change by a percent',
  'whatIf.step.redirect': 'When {debt} ends',
  'whatIf.step.redirectSummary': 'When {debt} ends, send its {amount} to {investment} from {date}',
  'whatIf.step.debt': 'When this ends',
  'whatIf.step.into': 'Pay it into',
  'whatIf.step.item': 'Item',
  'whatIf.step.byAria': 'Change {item} by a percent',
  'whatIf.step.starts': 'From',
  'whatIf.step.today': 'Today',
  'whatIf.step.from': 'from {date}',
  'whatIf.step.fromToday': 'from today',
  'whatIf.step.amounts': '{before} → {after}',
  'whatIf.step.skipped': 'This change no longer fits your plan, so it is left out.',
  'whatIf.step.more': 'More changes',
  'whatIf.presets': 'Ideas',
  'whatIf.share.title': 'Share this scenario',
  'whatIf.share.name': 'Title',
  'whatIf.share.defaultName': 'My what-if',
  'whatIf.share.showAmounts': 'Show amounts',
  'whatIf.share.hint': 'Off: only dates and percentages are shared.',
  'whatIf.share.create': 'Create link',
  'whatIf.share.created': 'Link ready',
  'whatIf.back': 'Close',

  'connect.eyebrow': 'Connections & sharing',
  'connect.title': 'Connections & sharing',
  'connect.subtitle': 'Control what can read and change your plan, and what you’ve shared with others',
  'connect.ai': 'AI assistants',
  'connect.ai.readOnly': 'Read only',
  'connect.ai.canChange': 'Can change your plan',
  'connect.ai.since': 'Connected {date}',
  'connect.ai.endsOn': 'access ends {date}',
  'connect.ai.neverUsed': 'Not used yet',
  'connect.ai.loadFailed': 'Couldn’t load your connections.',
  'connect.ai.disconnectTitle': 'Disconnect {name}?',
  'connect.ai.disconnectBody': '{name} loses access to your plan straight away. You can connect it again whenever you like.',
  'connect.ai.lastUsed': 'Last used {when}',
  'connect.ai.notConnected': 'Not connected',
  'connect.ai.unavailable': 'Coming soon to the private alpha',
  'connect.ai.connect': 'Connect',
  'connect.ai.disconnect': 'Disconnect',
  'connect.ai.disconnectLabel': 'Disconnect {name}',
  'connect.ai.disconnected': '{name} disconnected',
  'connect.ai.connectLabel': 'Connect {name}',
  'connect.ai.url': 'Connector URL',
  'connect.ai.copiedHint': 'Connector URL copied. Add it as a custom connector in the assistant’s settings.',
  'connect.ai.urlHint': 'Add this as a custom connector in Claude or ChatGPT. It’s a different address from this web app’s.',
  'connect.ai.urlUnavailable': 'The connector address isn’t available right now. Reload the page to try again.',
  'connect.shares': 'Shared links',
  'connect.shares.none': 'Links you share from What If appear here.',
  'connect.shares.col.link': 'Link',
  'connect.shares.col.amounts': 'Amounts',
  'connect.shares.col.views': 'Views',
  'connect.shares.col.expires': 'Expires',
  'connect.shares.col.actions': 'Actions',
  'connect.shares.hidden': 'Amounts hidden',
  'connect.shares.shown': 'Amounts shown',
  'connect.shares.views': 'Viewed {n} times',
  'connect.shares.viewsOne': 'Viewed once',
  'connect.shares.viewsNone': 'Not viewed yet',
  'connect.shares.never': 'Never expires',
  'connect.shares.inDays': 'Expires in {n} days',
  'connect.shares.expired': 'Expired',
  'connect.shares.revoked': 'Revoked',
  'connect.shares.revoke': 'Revoke',
  'connect.shares.revokeLabel': 'Revoke link {name}',
  'connect.shares.create': 'Create shared link',
  'connect.history': 'Recent changes',
  'connect.history.none': 'No changes yet.',
  'connect.history.restored': 'Restored version {n}',
  'connect.account': 'Account',
  'connect.account.apple': 'Signed in with Apple',
  'connect.account.email': 'Signed in as {email}',
  'connect.account.export': 'Export my data',
  'connect.account.delete': 'Delete account and cloud data',
  'connect.account.deleteConfirm': 'This deletes your account, plan, history, waypoints and shared links from the cloud. Your iPhone data is untouched. Type DELETE to confirm.',
  'connect.account.deleteWord': 'DELETE',
  'connect.account.deleteField': 'Type DELETE to confirm',
  'connect.account.note': 'Your plan on the web is kept by Map My Money. The iPhone app keeps its own separate copy on your phone for now.',

  'settings.title': 'Settings',
  'settings.eyebrow': 'Your plan and account',
  'settings.plan': 'Plan settings',
  'settings.startingBalance': 'Starting balance',
  'settings.startingBalance.asOf': 'Your cash on {date}',
  'settings.startingBalance.monthStart': 'Your cash as this month began',
  'settings.expectedReturn': 'Expected return (%/yr)',
  'settings.withdrawalRate': 'Safe withdrawal rate (%)',
  'settings.timeframe': 'Default timeframe (years)',
  'settings.presentation.title': 'How should answers look?',
  'settings.presentation.note': 'Written answers always include every number, the date and a link to the same view here.',
  'settings.presentation.auto': 'Auto',
  'settings.presentation.auto.hint': 'Let your assistant decide',
  'settings.presentation.text': 'Text only',
  'settings.presentation.text.hint': 'Short written answers, no visuals',
  'settings.presentation.visual': 'Rich visuals',
  'settings.presentation.visual.hint': 'Charts and cards when your assistant supports them',
  'settings.timeZone.title': 'Time zone',
  'settings.timeZone.hint': 'Which day “today” is for your map, Claude and shared links.',
  'settings.timeZone.error': 'We couldn’t save your time zone, so it’s back as it was. Try again in a moment.',
  'settings.presentation.error': 'We couldn’t save that, so your choice is back as it was. Try again in a moment.',
  'settings.demo.title': 'Demo data',
  'settings.demo.hint': 'Mock builds only. Choosing a plan reloads the app with a fresh copy of it.',

  'signin.title': 'Sign in to Map My Money',
  'signin.subtitle': 'Your plan, on the big screen. Private alpha.',
  'signin.apple': 'Sign in with Apple',
  'signin.dev': 'Development sign-in',
  'signin.email': 'Email',
  'signin.devSubmit': 'Sign in with email',
  'signin.inviteOnly': 'Map My Money on the web is invite-only for now. Ask for an invite and we’ll let you in.',
  'signin.failed': 'Sign-in failed. Try again.',
  'signin.mode': 'Account',
  'signin.mode.signIn': 'Sign in',
  'signin.mode.signUp': 'Create account',
  'signin.titleSignUp': 'Create your account',
  'signin.subtitleSignUp': 'Invite-only for now. Use the email you were invited with.',
  'signin.password': 'Password',
  'signin.passwordShow': 'Show password',
  'signin.passwordHide': 'Hide password',
  'signin.passwordShowShort': 'Show',
  'signin.passwordHideShort': 'Hide',
  'signin.passwordHint': '12 to 128 characters.',
  'signin.passwordNote': 'Keep this password safe - there is no reset yet.',
  'signin.passwordTooShort': 'Use at least 12 characters.',
  'signin.passwordTooLong': 'Use 128 characters or fewer.',
  'signin.submitSignIn': 'Sign in',
  'signin.submitSignUp': 'Create account',
  'signin.pendingSignIn': 'Signing in...',
  'signin.pendingSignUp': 'Creating account...',
  'signin.or': 'or',
  'signin.rateLimited': 'Too many attempts. Try again in a few minutes.',
  'signin.inviteOnlyEmail': 'This email is not on the invite list yet.',
  'signin.devForm': 'Development sign-in',
  'signin.footer': 'Not financial advice · mapmymoney.app',

  'empty.eyebrow': 'Welcome',
  'empty.title': 'Start your plan',
  'empty.body': 'Two ways in. Pick one now and mix them later.',
  'empty.example.title': 'Start from an example household',
  'empty.example.body': 'A UK household with pay, a home, a car loan and a pension. Change anything to make it yours.',
  'empty.example': 'Use this example',
  'empty.talk.title': 'Talk to your assistant',
  'empty.talk.body': 'Connect Claude or ChatGPT, then just say your income and spending. Nothing is added until you tick it.',
  'empty.talk.cta': 'Connect an assistant',

  'error.title': 'We couldn’t load your plan',
  'error.body': 'Check your connection and try again.',
  'error.boundary': 'Something went wrong on this screen.',
  'error.saveFailed': 'Your change wasn’t saved. We put things back as they were.',
  'error.stale': 'Your plan changed on another device, so this edit wasn’t saved. You’re now seeing the latest version.',
  'error.invalid': 'That change isn’t valid: {detail}',
  'error.rateLimited': 'Too many changes at once. Wait a moment and try again.',
  'error.notFound': 'Page not found',
  'error.backHome': 'Back to Home',

  'share.brand': 'Shared scenario',
  'share.by': 'by {name}',
  'share.sooner': '{span} sooner',
  'share.later': '{span} later',
  'share.nowReachable': 'Now within reach',
  'share.noChange': 'Same timeline',
  'share.before': 'Before',
  'share.after': 'With this change',
  'share.earlier': '{span} earlier',
  'share.netWorthIn': 'Net worth in {years}y',
  'share.amountsHidden': 'Amounts hidden by {name}. Only dates and percentages are shared.',
  'share.try': 'Try this with your numbers',
  'share.getApp': 'Get Map My Money for iPhone',
  'share.footer': 'Not financial advice · mapmymoney.app',
  'share.goal.netWorth': 'Net worth goal',
  'share.goal.investment': 'Investing goal',
  'share.goal.cash': 'Savings goal',
  'share.goal.debt': 'Debt free',
  'share.goal.passive': 'Passive income goal',
  'share.missing': 'This link has been turned off or has expired',
  'share.chart': '{goal}: {before} before, {after} with this change',
  'share.chartValues': 'Net worth over {years} years, before and with this change',

  'smap.docTitle': '{title} · Map My Money',
  'smap.sharedBy': 'Shared by {name}',
  'smap.sharedWithYou': 'Shared with you',
  'smap.viewOnly': 'View only',
  'smap.noSignIn': 'No sign-in needed',
  'smap.meta': '{years}-year forecast · as of {date}',
  'smap.meta.horizon': '{years}-year forecast',
  'smap.templateBanner.title': 'Template:',
  'smap.templateBanner.body': 'the shape of a plan with every amount removed. Put in your own numbers once you make it yours.',
  'smap.story.growth': 'On this map, net worth {growth} in {years} years',
  'smap.story.mortgage': ', the mortgage is paid off by {date}',
  'smap.story.goal': ' and ‘{goal}’ is reached by {date}',
  'smap.story.end': '.',
  'smap.growth.doubles': 'roughly doubles',
  'smap.growth.triples': 'roughly triples',
  'smap.growth.times': 'grows to about {x} times today’s',
  'smap.growth.percent': 'grows by about {pct}',
  'smap.growth.falls': 'falls by about {pct}',
  'smap.netWorthToday': 'Net worth today',
  'smap.netWorthOn': 'Net worth on {date}',
  'smap.asOf': 'As of {date}',
  'smap.asOf.note': 'A snapshot of {name}’s map on that day. It doesn’t update.',
  'smap.asOf.noteAnon': 'A snapshot of this map on that day. It doesn’t update.',
  'smap.goals.title': 'Goals',
  'smap.inYears': 'In {n} years',
  'smap.yourNumber': 'Your number',
  'smap.times': '{x}×',
  'smap.timesToday': '{x}× today',
  'smap.chart.title': 'Net worth, month by month',
  'smap.chart.hint': 'Drag across the chart, or focus it and use the arrow keys',
  'smap.chart.hintTemplate': 'Shown as multiples of today’s net worth. Drag across the chart, or focus it and use the arrow keys',
  'smap.range': 'Range',
  'smap.range.option': '{n}y',
  'smap.readout.thisMonth': 'this month',
  'smap.readout.in': 'in {span}',
  'smap.series.netWorth': 'Net worth',
  'smap.series.shared': 'Shared map',
  'smap.series.cash': 'Cash',
  'smap.series.investments': 'Investments',
  'smap.series.home': 'Home & assets',
  'smap.series.owed': 'Mortgage & loans',
  'smap.series.milestone': 'Milestone',
  'smap.paidOff': 'Paid off',
  'smap.scrubber': 'Net worth by month',
  'smap.scrubber.value': '{date}: net worth {value}',
  'smap.pin': '{label}, {date}',
  'smap.table.show': 'Show as a table',
  'smap.table.hide': 'Hide the table',
  'smap.table.netWorth': 'Net worth now and at the end of each year',
  'smap.table.date': 'Date',
  'smap.play.title': 'Play with it',
  'smap.play.sub': 'Try your own changes on top of this map.',
  'smap.play.spend': 'Spend {amount} less each month',
  'smap.play.invest': 'Invest {amount} more each month',
  'smap.play.overpay': 'Overpay the mortgage {amount} a month',
  'smap.play.spendPlain': 'Spend less each month',
  'smap.play.investPlain': 'Invest more each month',
  'smap.play.overpayPlain': 'Overpay the mortgage each month',
  'smap.play.private': 'Your changes stay on your device. Nothing is saved or sent.',
  'smap.play.disabled': 'Add your own numbers to play with this.',
  'smap.delta.title': 'Compared with the shared map',
  'smap.delta.yours': 'Your version',
  'smap.delta.same': 'Same as shared',
  'smap.delta.netWorth': 'Net worth in 10 years',
  'smap.delta.mortgageFree': 'Mortgage paid off',
  'smap.delta.biggest': 'Biggest move',
  'smap.delta.sooner': '{span} sooner',
  'smap.delta.later': '{span} later',
  'smap.delta.dateMove': '{date} ({move})',
  'smap.delta.moved': '{label}, {move}',
  'smap.delta.outOfView': 'Out of view',
  'smap.flow.title': 'Monthly cash flow',
  'smap.flow.sub': 'The next 12 months',
  'smap.flow.subTemplate': 'The next 12 months, as a share of income',
  'smap.flow.income': 'Income',
  'smap.flow.spending': 'Spending',
  'smap.flow.spendingAll': 'Spending & repayments',
  'smap.flow.debt': 'Mortgage & loans',
  'smap.flow.invested': 'Invested',
  'smap.flow.left': 'Left over',
  'smap.flow.label': 'Income and where it goes each month, {from} to {to}',
  'smap.flow.month': 'Month',
  'smap.flow.percent': '{pct}',
  'smap.ms.title': 'Milestones',
  'smap.ms.sub': 'In date order, with your changes',
  'smap.ms.subTemplate': 'In date order',
  'smap.ms.none': 'No milestones in the next {years} years.',
  'smap.ms.meaning.mortgageFree': 'The last mortgage payment. From then on that money is free each month.',
  'smap.ms.meaning.debtFree': 'Nothing left owed on the debts this map counts.',
  'smap.ms.meaning.retire': 'Investments can pay the income set aside for retiring.',
  'smap.ms.meaning.goal': 'A goal set on this map.',
  'smap.ms.same': 'Same as shared',
  'smap.ms.outOfView': 'Out of view',
  'smap.milestone.mortgageFree': 'Mortgage paid off',
  'smap.key.title': 'Key numbers',
  'smap.key.on': 'Net worth on {date}',
  'smap.key.inYears': 'Net worth in {n} years',
  'smap.key.saved': 'Saved or invested, next 12 months',
  'smap.key.mortgageFree': 'Mortgage paid off',
  'smap.key.was': 'was {value}',
  'smap.key.notInView': 'Not within {n} years',
  'smap.structure.title': 'What’s on this map',
  'smap.structure.sub': 'Every item, without its amount.',
  'smap.structure.payDay': 'day {n}',
  'smap.structure.since': 'since {date}',
  'smap.structure.from': 'from {date}',
  'smap.cta.title': 'Make this map yours',
  'smap.cta.body': 'Start from {title}, put in your own numbers and see where you are heading.',
  'smap.cta.inviteOnly': 'Invite-only for now',
  'smap.cta.yours': 'Your copy, your numbers',
  'smap.cta.email': 'Email',
  'smap.cta.emailPlaceholder': 'you@example.com',
  'smap.cta.join': 'Get early access',
  'smap.cta.privacy': 'We’ll email you when it’s ready. No account is created; this only joins the waitlist, and we use your email for nothing else.',
  'smap.cta.joined': 'You’re on the list. We’ll email you when it’s ready.',
  'smap.cta.emailInvalid': 'Enter an email address like name@example.com.',
  'smap.cta.rateLimited': 'Too many tries. Wait a minute and try again.',
  'smap.cta.joinFailed': 'That didn’t go through. Check your connection and try again.',
  'smap.cta.copy': 'Make my own copy',
  'smap.cta.copyNote': 'Creates a plan in your account you can change. Nothing you do changes the original.',
  'smap.cta.copySignIn': 'You’ll sign in or create an account first, then come straight back here.',
  'smap.cta.copying': 'Making your copy…',
  'smap.cta.copied': 'Saved to your plans as “{name}”.',
  'smap.cta.copiedNeedsNumbers': 'Saved to your plans as “{name}”. Open it and fill in your numbers to make it yours.',
  'smap.cta.open': 'Open it',
  'smap.cta.copiedPrimary': 'This map is now your plan.',
  'smap.cta.copiedPrimaryTemplate': 'This template is now your plan. Fill in your numbers in Plan to make it yours.',
  'smap.cta.planLimit': 'You already have {limit} plans, the most you can keep. Delete one, then make your copy.',
  'smap.cta.gone': 'This link can no longer be copied. It may have been turned off.',
  'smap.cta.copyFailed': 'Your copy wasn’t made. Check your connection and try again.',
  'smap.cta.how': 'How was this made?',
  'smap.cta.dock': 'Make this map yours',
  'smap.footer.sharedBy': 'Shared by {name}',
  'smap.footer.expires': 'Link expires {date}',
  'smap.footer.noExpiry': 'Link doesn’t expire',
  'smap.footer.estimates': 'Forecasts are estimates, not financial advice',
  'smap.footer.report': 'Report this link',
  'smap.footer.reported': 'Thanks. We’ll look at this link.',
  'smap.missing.body': 'Ask the person who shared it for a new link, or start a map of your own.',
  'smap.missing.cta': 'See what Map My Money does',
  'smap.error.title': 'We couldn’t open this link',
  'smap.error.body': 'Check your connection and try again.',
  'smap.loading': 'Loading the shared map…',

  'shareNew.eyebrow': 'Share',
  'shareNew.title': 'Share your map',
  'shareNew.subtitle': 'Choose what someone opening your link will see.',
  'shareNew.kinds': 'What to share',
  'shareNew.kind.showcase': 'Showcase',
  'shareNew.kind.map': 'Map',
  'shareNew.kind.template': 'Template',
  'shareNew.kind.showcase.body': 'A what-if result. Amounts hidden or shown. Can’t be copied.',
  'shareNew.kind.map.body': 'Your plan with your numbers. Can be copied. Expires in 14 days.',
  'shareNew.kind.template.body': 'Your plan’s shape without any numbers. Can be copied.',
  'shareNew.name': 'Title',
  'shareNew.defaultTitle.map': 'My map',
  'shareNew.defaultTitle.template': 'My map, as a template',
  'shareNew.expiry': 'Link expires after',
  'shareNew.days': '{n} days',
  'shareNew.never': 'No expiry',
  'shareNew.mapWarning': 'Anyone with this link can read these numbers. A shared map is frozen and cannot be edited: to change it, publish a new link. Anyone with an account can make their own copy, and their changes never touch yours. Labels you typed are visible too.',
  'shareNew.understand': 'I understand it cannot be edited and can be copied',
  'shareNew.templateNote': 'Every amount is removed before the link is made. Item names you typed are visible.',
  'shareNew.showcaseNote': 'A showcase shows what one change does to your forecast. Try a change in What If, then choose Share.',
  'shareNew.openWhatIf': 'Open What If',
  'shareNew.create': 'Create link',
  'shareNew.creating': 'Creating…',
  'shareNew.ready': 'Link ready',
  'shareNew.another': 'Make another link',
  'shareNew.preview': 'Preview',
  'shareNew.previewHint': 'What someone opening the link sees.',
  'shareNew.links': 'Your links',
  'shareNew.none': 'Links you make appear here.',
  'shareNew.views': 'about {n} views',
  'shareNew.viewsOne': 'about 1 view',
  'shareNew.viewsNone': 'no views yet',
  'shareNew.revoke': 'Revoke',
  'shareNew.revokeLabel': 'Revoke link {name}',
  'shareNew.revokeTitle': 'Revoke this link?',
  'shareNew.revokeBody': 'Anyone who opens “{name}” will see that it has been turned off. Copies people already made stay theirs.',
  'shareNew.revokeConfirm': 'Revoke link',
  'shareNew.revoked': 'Link revoked',
  'shareNew.error.limit': 'You have {n} live links, the most you can have. Revoke one to make another.',
  'shareNew.error.rate': 'You’ve made a lot of links in the last hour. Try again later.',
  'shareNew.error.invalid': 'This plan can’t be shared as a link: {detail}',
  'shareNew.error.generic': 'The link wasn’t created. Check your connection and try again.',
  'shareNew.noPlan': 'Add something to your plan before sharing it.',

  'plans.title': 'Plans',
  'plans.subtitle': 'Your plan, copies of it, and plans people share with you.',
  'plans.yours': 'Yours',
  'plans.sharedWithYou': 'Shared with you',
  'plans.notCounted': 'Not counted',
  'plans.sharedEmpty.title': 'Nothing shared with you yet',
  'plans.sharedEmpty.body': 'When someone adds you to their plan, it appears here at the role they chose.',
  'plans.cap': '{used} of {max}',
  'plans.capWord': 'plans',
  'plans.capLabel': '{used} of {max} plans used',
  'plans.capFull': 'Plan limit reached. Delete a plan to add another.',
  'plans.new': 'New plan',
  'plans.role.owner': 'Owner',
  'plans.role.editor': 'Editor',
  'plans.role.viewer': 'Viewer',
  'plans.primary': 'Primary',
  'plans.kind.primary': 'Primary plan',
  'plans.kind.yours': 'Your plan',
  'plans.kind.shared': 'Shared with you',
  'plans.copiedFrom': 'Copied from {name}',
  'plans.copiedFromGone': 'A copy',
  'plans.ownedBy': 'Owned by {name}',
  'plans.updated': 'Updated {when}',
  'plans.empty': 'Nothing in it yet',
  'plans.openNow': 'Open now',
  'plans.openLabel': 'Open {name}',
  'plans.rename': 'Rename',
  'plans.renameLabel': 'Rename {name}',
  'plans.delete': 'Delete',
  'plans.deleteLabel': 'Delete {name}',
  'plans.how.title': 'How plans work',
  'plans.how.primary.title': 'One primary plan',
  'plans.how.primary.body': 'Your own money. Assistants and the iPhone app open it first.',
  'plans.how.copy.title': 'Copies',
  'plans.how.copy.body': 'A plan you own that started from another. Changes never flow back.',
  'plans.how.shared.title': 'Shared with you',
  'plans.how.shared.body': 'Someone else’s plan, at the role they gave you. It doesn’t count towards your {max}.',
  'plans.how.shared.bodyNoLimit': 'Someone else’s plan, at the role they gave you. It doesn’t count towards your own plans.',
  'plans.how.links.title': 'Shared links',
  'plans.how.links.body': 'Frozen snapshots for anyone with the link. Share from whichever plan is open.',
  'plans.new.title': 'New plan',
  'plans.new.used': '{used} of {max} plans used',
  'plans.new.how': 'How to start',
  'plans.new.blank': 'Start blank',
  'plans.new.blankBody': 'An empty plan. Fill it in yourself or by talking to your assistant.',
  'plans.new.copy': 'Copy a plan',
  'plans.new.copyBody': 'A copy you own. It remembers where it came from; changes never flow back.',
  'plans.new.from': 'Copy from',
  'plans.new.fromShared': '{name} ({owner}’s)',
  'plans.new.name': 'Name',
  'plans.new.nameHint': 'Up to {max} characters.',
  'plans.new.nameRequired': 'Give the plan a name.',
  'plans.new.nameTooLong': 'Use {max} characters or fewer.',
  'plans.new.nameInvalid': 'Use letters, numbers and punctuation only.',
  'plans.new.create': 'Create plan',
  'plans.new.creating': 'Creating…',
  'plans.new.defaultName': 'New plan',
  'plans.new.copyName': '{name} copy',
  'plans.created': '“{name}” is ready. You’re in it now.',
  'plans.rename.title': 'Rename plan',
  'plans.rename.save': 'Save name',
  'plans.renamed': 'Renamed to “{name}”',
  'plans.delete.title': 'Delete “{name}”?',
  'plans.delete.body': 'Its history, check-ins and shared links go with it, and anyone you added loses access. Copies made from it stay. This can’t be undone.',
  'plans.delete.confirm': 'Delete plan',
  'plans.deleted': '“{name}” deleted',
  'plans.error.limit': 'You can have {n} plans. Delete one you no longer need, then try again.',
  'plans.error.notFound': 'That plan is no longer available. It may have been deleted or unshared.',
  'plans.error.forbidden': 'Your role on this plan doesn’t allow that.',
  'plans.error.generic': 'That didn’t go through. Check your connection and try again.',
  'plans.readOnly.toast': 'You can view this plan but not change it.',
  'plans.readOnly.reason': 'View only. {name} can change this plan.',
  'plans.readOnly.reasonAnon': 'View only. The owner can change this plan.',
  'plans.readOnly.copy': 'Make my own copy',
  'plans.readOnly.copied': 'Your copy “{name}” is open. Change anything you like.',
  'plans.switcher.label': 'Plan: {name}',
  'plans.switcher.menu': 'Switch plan',
  'plans.switcher.manage': 'Manage plans',
  'plans.settings.title': 'Plans',
  'plans.settings.one': 'You have one plan. Make another to try a different future side by side.',
  'plans.settings.many': '{n} plans. “{name}” is open.',
  'people.action': 'People',
  'people.actionLabel': 'People on {name}',
  'people.title': 'People on “{name}”',
  'people.intro.owner': 'Everyone here can see this plan. Editors can change it too; viewers can only look.',
  'people.intro.member': '{owner} decides who is on this plan and what they can do.',
  'people.loading': 'Loading people…',
  'people.listTitle': 'On this plan',
  'people.onlyOwner': 'Just the owner',
  'people.sharedWith.one': 'Shared with 1 person',
  'people.sharedWith.many': 'Shared with {n} people',
  'people.empty': 'Only you so far. Add someone by email to plan together.',
  'people.you': 'You',
  'people.canWhatIf': 'Their assistant can try what-ifs',
  'people.done': 'Done',
  'people.add.title': 'Add someone',
  'people.add.email': 'Email',
  'people.add.placeholder': 'name@example.com',
  'people.add.emailHint': 'They need a Map My Money account.',
  'people.add.role': 'Role',
  'people.role.viewerHint': 'Can look at the plan, not change it.',
  'people.role.editorHint': 'Can change the plan, like you.',
  'people.add.whatIfs': 'Let their assistant try what-ifs',
  'people.add.whatIfsHint': 'Their AI assistant can model ideas on this plan. Nothing is saved.',
  'people.add.submit': 'Add',
  'people.add.adding': 'Adding…',
  'people.added.viewer': '{name} can now view this plan.',
  'people.added.editor': '{name} can now change this plan.',
  'people.roleFor': 'Role for {name}',
  'people.whatIfs': 'Assistant can try what-ifs',
  'people.whatIfsFor': 'Let {name}’s assistant try what-ifs',
  'people.whatIfs.on': '{name}’s assistant can now try what-ifs.',
  'people.whatIfs.off': '{name}’s assistant can no longer try what-ifs.',
  'people.updated.viewer': '{name} can now only view this plan.',
  'people.updated.editor': '{name} can now change this plan.',
  'people.removeLabel': 'Remove {name}',
  'people.remove.body': 'Remove {name}? They lose access straight away, and links they made from this plan stop working.',
  'people.remove.confirm': 'Remove {name}',
  'people.removed': '{name} no longer has access.',
  'people.leave': 'Leave plan',
  'people.leave.body': 'Leave “{name}”? It disappears from your plans until {owner} adds you again, and links you made from it stop working.',
  'people.leave.confirm': 'Leave plan',
  'people.left': 'You left “{name}”.',
  'people.error.emailRequired': 'Enter their email address.',
  'people.error.emailInvalid': 'That doesn’t look like an email address.',
  'people.error.notFound': 'No account can be added with that email. They need a Map My Money account first.',
  'people.error.self': 'That’s you. You own this plan already.',
  'people.error.already': '{name} is already on this plan. Change their role below.',
  'people.error.conflict': 'That person is already on this plan.',
  'people.error.rateLimited': 'That’s a lot of changes to people in a short time. Wait a few minutes, then try again.',
  'people.error.forbidden': 'Only the plan’s owner can change who is on it.',
  'people.error.load': 'Couldn’t load the people on this plan.',

  'firstRun.eyebrow': 'New plan',
  'firstRun.title': '“{name}” is empty',
  'firstRun.body': 'Fill it in the way that suits you. You can mix them.',
  'firstRun.add.title': 'Add items yourself',
  'firstRun.add.body': 'Income, spending, savings and debts, one at a time.',
  'firstRun.add.cta': 'Add your first item',
  'firstRun.talk.title': 'Talk to your assistant',
  'firstRun.talk.body': 'Tell Claude or ChatGPT about your money in your own words. Nothing is added until you tick it.',
  'firstRun.talk.cta': 'Connect an assistant',
  'firstRun.shared.title': 'Start from a shared map',
  'firstRun.shared.body': 'Opened someone’s shared map? Choose “Make my own copy” on it to start from their plan.',
  'firstRun.viewer': 'Nothing has been added to this plan yet. {name} can fill it in.',
  'firstRun.summary': 'Started “{name}”',

  'drafts.banner': '{name} prepared a share for you. Nothing is public until you confirm.',
  'drafts.bannerMany': '{name} prepared {n} shares for you. Nothing is public until you confirm.',
  'drafts.review': 'Review',
  'drafts.reviewLabel': 'Review “{title}”',
  'drafts.assistant': 'Your assistant',

  'draft.eyebrow': 'Confirm a share',
  'draft.title': 'Publish “{title}”?',
  'draft.subtitle': '{name} prepared this link. It stays private until you confirm it here.',
  'draft.details': 'Details',
  'draft.preparedBy': 'Prepared by',
  'draft.plan': 'From plan',
  'draft.kind': 'Kind',
  'draft.linkExpires': 'Link expires',
  'draft.linkExpiresValue': '{n} days after you confirm',
  'draft.draftExpires': 'Draft expires',
  'draft.public': 'What becomes public',
  'draft.map.1': 'Every amount, balance and rate in this plan, as it is now.',
  'draft.map.2': 'Anyone with an account can make their own copy.',
  'draft.template.1': 'The plan’s shape: item names, kinds, schedules and dates.',
  'draft.template.2': 'No amount, balance or rate. The curves are relative to today.',
  'draft.frozen': 'Frozen once published. To change it, publish a new link.',
  'draft.confirm': 'Confirm and create link',
  'draft.confirming': 'Creating link…',
  'draft.discard': 'Discard',
  'draft.discarded': 'Draft discarded. Nothing was published.',
  'draft.preview': 'Preview',
  'draft.previewHint': 'What someone opening the link will see.',
  'draft.doneBody': 'Send it to whoever you like. You can revoke it any time from Share.',
  'draft.missing.title': 'This draft is no longer available',
  'draft.missing.body': 'It expired, was already confirmed or discarded, or belongs to another account. Ask your assistant to prepare it again, or make a link yourself.',
  'draft.missing.cta': 'Go to Share',
  'draft.error.title': 'We couldn’t load this draft',
  'draft.error.confirm': 'The link wasn’t created. Check your connection and try again.',
  'draft.error.discard': 'The draft wasn’t discarded. Check your connection and try again.',

  'shareNew.from': 'Sharing from',
  'shareNew.templateFrozen': 'Frozen once published. To change it, publish a new link.',
  'shareNew.viewOthers': 'View it as others will see it',
  'shareNew.viewerBlocked': 'Viewers can’t publish links from this plan. Ask {name} to share it, or make your own copy first.',

  'smap.own.title': 'What you get with your own copy',
  'smap.own.sub': 'Connect Claude or ChatGPT and your copy answers back.',
  'smap.own.ask.title': 'Ask it anything',
  'smap.own.ask.1': 'When will we be debt free?',
  'smap.own.ask.2': 'What if we overpay the mortgage by 300 a month?',
  'smap.own.ask.3': 'Can we afford a 25,000 car next year?',
  'smap.own.ask.4': 'What changed since last month?',
  'smap.own.ask.5': 'Are we on track to retire at 60?',
  'smap.own.ask.6': 'How long until the emergency fund is full?',
  'smap.own.talk.title': 'Add things by talking',
  'smap.own.talk.1': 'Our rent is 950 on the 1st',
  'smap.own.talk.2': 'Alex gets paid 3,100 on the 28th',
  'smap.own.talk.3': 'We put 500 a month into the ISA',
  'smap.own.talk.note': 'Claude lists what it heard; nothing is added until you tick it.',
  'smap.own.anywhere.title': 'See it anywhere',
  'smap.own.anywhere.body': 'The same plan in the chat, on the web and on iPhone. Change it in one, see it in all three.',
  'smap.own.anywhere.chat': 'Chat',
  'smap.own.anywhere.web': 'Web',
  'smap.own.anywhere.phone': 'iPhone',

  'month.short.1': 'Jan',
  'month.short.2': 'Feb',
  'month.short.3': 'Mar',
  'month.short.4': 'Apr',
  'month.short.5': 'May',
  'month.short.6': 'Jun',
  'month.short.7': 'Jul',
  'month.short.8': 'Aug',
  'month.short.9': 'Sep',
  'month.short.10': 'Oct',
  'month.short.11': 'Nov',
  'month.short.12': 'Dec',

  'chart.yearN': 'Y{n}',
  'span.years': '{n}yr',
  'span.months': '{n}mo',
  'span.yearsMonths': '{y}yr {m}mo',
  'span.yearsLong': '{n} years',
  'span.yearLong': '1 year',
  'span.monthsLong': '{n} months',
  'span.monthLong': '1 month',
  'span.weeks': '{n} weeks',
  'span.week': '1 week',
  'span.days': '{n} days',
  'span.day': '1 day',

  'stamp.label': 'Which result this is',
  'stamp.line': 'Plan v{version} · as of {date} · result {hash}',
  'stamp.lineNoHash': 'Plan v{version} · as of {date}',
  'chat.banner.label': 'From your chat',
  'chat.banner.title': 'This is the view your assistant showed you',
  'chat.banner.same': 'Same as in your chat.',
  'chat.banner.sameVersion': 'Same plan version as in your chat.',
  'chat.banner.versionChanged': 'The plan has changed since (now v{version}).',
  'chat.banner.waypointChanged': 'Your balances have been updated since: a newer waypoint is in the plan.',
  'chat.banner.different': 'Different from your chat result.',
  'chat.banner.otherDay': 'Your chat’s figures were as of {chat}; this page shows {today}.',
  'chat.replay.invalidChanges': 'The changes in this link couldn’t be read, so they aren’t shown here. Ask your assistant for the link again.',
  'chat.replay.invalidPurchase': 'The purchase in this link couldn’t be read, so it isn’t shown here. Ask your assistant for the link again.',
  'chat.replay.notApplicable': 'These changes no longer fit your plan (an item they change may have been removed), so they can’t be replayed here.',
  'chat.replay.eyebrow': 'From your chat',
  'chat.replay.changes': 'Changes from your chat',
  'chat.replay.startOver': 'Start a new what-if',
  'chat.replay.before': 'Current plan in {years}Y',
  'chat.replay.after': 'With changes in {years}Y',
  'chat.replay.difference': 'Difference',
  'chat.replay.goals': 'Goals that move',
  'chat.replay.noGoalsMove': 'No goal dates move.',
  'chat.replay.goalUnreachable': 'No longer reached in {years} years',
  'chat.replay.chart': 'Net worth over {years} years: current plan reaches {baseline}, with these changes {modified}.',
  'buy.eyebrow': 'Should I buy this?',
  'buy.title': 'Should I buy {item}?',
  'buy.price': 'Price',
  'buy.share': 'Of monthly disposable income',
  'buy.shareNone': 'No disposable income to save from',
  'buy.disposable': 'Monthly disposable income',
  'buy.save': 'Time to save it',
  'buy.saveRow': '{percent}% of disposable ({amount} a month)',
  'buy.overTenYears': 'Over 10 years',
  'buy.invested': 'If invested instead at {rate}% a year',
  'buy.investedRow': 'After {years} years',
  'buy.investedRowOne': 'After 1 year',
  'buy.investedValue': '{value} ({growth} growth)',
  'buy.ifBought': 'If bought on {date}',
  'chat.banner.dismiss': 'Dismiss',
  'chat.planMissing.title': 'This plan isn’t available',
  'chat.planMissing.body': 'The link from your chat points to a plan you can’t open with this account. It may have been deleted or not shared with you.',
  'debts.debtFree': 'Debt free (except the mortgage)',
  'debts.mortgageFree': 'Mortgage paid off',
  'debts.title': 'Debt milestones',
  'chat.banner.seeChanges': 'See what changed',

  'ask.title': 'Ask your assistant',
  'ask.collapse': 'Hide example questions',
  'ask.expand': 'Show example questions',
  'ask.copyLabel': 'Copy question',
  'ask.copied': 'Copied',
  'ask.selected': 'Selected: copy it with your keyboard or long-press.',
  'ask.footer': 'Open Claude or ChatGPT with Map My Money connected and paste it.',
  'ask.footerLink': 'Connections',
  'ask.connect': 'Connect an assistant',
  'ask.connectHint': 'Connect Claude or ChatGPT to ask questions like these about your plan.',
  'ask.home.northStar': 'How am I doing against my {goal} goal?',
  'ask.home.overall': 'How am I doing overall?',
  'ask.home.sinceLastMonth': 'What changed since last month?',
  'ask.home.debtFree': 'Am I on track to clear my debts?',
  'ask.home.focus': 'What should I focus on this month?',
  'ask.forecast.debtFree': 'When will I be debt free?',
  'ask.forecast.mortgageFree': 'When will my mortgage be paid off?',
  'ask.forecast.retire60': 'What would retiring at 60 change?',
  'ask.forecast.speedUp': 'Why does my net worth grow faster later on?',
  'ask.forecast.tenYears': 'Where will I be in ten years if nothing changes?',
  'ask.goals.furthest': 'Which goal is furthest away and why?',
  'ask.goals.sooner': 'What would get me to {goal} sooner?',
  'ask.goals.suggest': 'What goal should I set first?',
  'ask.goals.realistic': 'Are my goals realistic on my current plan?',
  'ask.whatIf.overpay': 'What if I overpaid the {debt} by 300 a month?',
  'ask.whatIf.mortgage': 'mortgage',
  'ask.whatIf.debt': 'debt',
  'ask.whatIf.saveMore': 'What if I saved 300 more a month?',
  'ask.whatIf.investMore': 'What if I put 100 more a month into {item}?',
  'ask.whatIf.investments': 'my investments',
  'ask.whatIf.investStart': 'What if I started investing 100 a month?',
  'ask.whatIf.car': 'Can I afford a new car next year?',
  'ask.plan.addRent': 'Add my rent: 950 on the 1st',
  'ask.plan.addPhone': 'Add my phone contract: 30 on the 1st',
  'ask.plan.statement': 'Here’s my bank statement: find my regular payments',
  'ask.plan.missing': 'Is anything missing from my plan?',

  'activity.title': 'Assistant activity',
  'activity.view': 'View',
  'activity.viewLabel': 'View the change “{summary}” in your history',
  'activity.undoLabel': 'Undo “{summary}”',
  'activity.undoTitle': 'Undo this change?',
  'activity.undoBody': '{assistant} changed your plan: “{summary}”. Undoing puts your plan back to version {version}. You can restore this change later from your history.',
  'activity.undoConfirm': 'Undo change',
  'activity.stale': 'Your plan changed again before the undo, so nothing was undone. The list now shows the latest changes.',

  'history.since': 'Showing changes after version {version}.',
  'history.showAll': 'Show all',
  'history.sinceNone': 'No changes after version {version}.',
  'history.by': 'by {name}',
  'history.byYou': 'by you',
  'docTitle.upgrade': 'Plans and pricing',
  'docTitle.billingSuccess': 'Payment',
  'docTitle.billingCancelled': 'Checkout cancelled',
  'docTitle.welcome': 'Welcome',

  'billing.tier.none': 'No subscription',
  'billing.tier.lapsed': 'Subscription ended',
  'billing.tier.plus': 'Plus',
  'billing.tier.coach': 'Coach',
  'billing.title': 'Plan & billing',
  'billing.loadFailed': 'Your billing details didn’t load.',
  'billing.renews': 'Renews on {date}',
  'billing.ends': 'Ends on {date}. You keep {tier} until then.',
  'billing.pastDue': 'We couldn’t take your last payment. You keep {tier} until {date} — update your card.',
  'billing.lapsed': 'Your subscription has ended. Your plans are read-only, and nothing has been deleted.',
  'billing.resubscribe': 'Resubscribe',
  'billing.interval.month': 'Billed monthly',
  'billing.interval.year': 'Billed yearly',
  'billing.source.comp': 'Included by Map My Money',
  'billing.source.compUntil': 'Included by Map My Money until {date}',
  'billing.source.revenuecat': 'Managed in the App Store',
  'billing.early': 'Early access: no limits apply yet.',
  'billing.earlyBody': 'Paid plans are on the way. Until they start, you have today’s limits and nothing changes.',
  'billing.manage': 'Manage billing',
  'billing.opening': 'Opening…',
  'billing.portalError': 'Billing didn’t open. Try again in a moment.',
  'billing.seePlans': 'See plans',
  'billing.upgrade': 'Upgrade',
  'billing.usage': 'Usage',
  'billing.usage.plans': 'Plans',
  'billing.usage.shares': 'Live links',
  'billing.usage.of': '{used} of {limit}',
  'billing.usage.noLimit': '{used} · no limit',
  'billing.usage.label': '{name}: {used} of {limit}',
  'billing.usage.readOnlyOne': '1 plan is read-only. Nothing has been deleted.',
  'billing.usage.readOnly': '{n} plans are read-only. Nothing has been deleted.',
  'billing.brand.title': 'Share page branding',
  'billing.brand.body': 'Links you share show “Shared by” your name, with a thin bar in your colour. Map My Money’s footer stays.',
  'billing.brand.name': 'Display name',
  'billing.brand.accent': 'Accent colour',
  'billing.brand.accentHex': 'Accent colour as hex',
  'billing.brand.save': 'Save branding',
  'billing.brand.saving': 'Saving…',
  'billing.brand.remove': 'Remove',
  'billing.brand.saved': 'Branding saved',
  'billing.brand.removed': 'Branding removed',
  'billing.brand.nameError': 'Use 1 to 60 characters.',
  'billing.brand.accentError': 'Use a colour like #00D4FF.',
  'billing.brand.error': 'Branding wasn’t saved. Try again in a moment.',
  'billing.brand.preview': 'Shared by {name}',

  'upgrade.cta': 'See plans',
  'upgrade.notNow': 'Not now',
  'upgrade.ok': 'OK',
  'upgrade.reassure': 'Nothing you have is deleted or hidden.',
  'upgrade.title.plans': 'Room for more plans',
  'upgrade.body.plans': '{tier} includes {limit} plans of your own, and you’re using them all. Upgrade to keep more side by side, or delete one you no longer need.',
  'upgrade.body.plans.noNumber': 'You’re using all the plans {tier} includes. Upgrade to keep more side by side, or delete one you no longer need.',
  'upgrade.title.shares': 'All your live links are in use',
  'upgrade.body.shares': '{tier} includes {limit} live links at a time. Revoke one you no longer need, or upgrade for more. Links you’ve shared keep working.',
  'upgrade.body.shares.noNumber': 'You’re using all the live links {tier} includes. Revoke one you no longer need, or upgrade for more.',
  'upgrade.title.share_expiry': 'Choosing how long a link lasts is part of Plus',
  'upgrade.body.share_expiry': 'On {tier}, links use the standard expiry. Upgrade to pick your own, or create this link with the standard one.',
  'upgrade.title.plan_people': 'Room for more people',
  'upgrade.body.plan_people': '{tier} lets you share a plan with {limit} people. Upgrade to add more. Everyone already on it keeps their access.',
  'upgrade.body.plan_people.noNumber': 'This plan is shared with as many people as {tier} allows. Upgrade to add more.',
  'upgrade.title.editors': 'Planning together is part of Plus',
  'upgrade.body.editors': 'On {tier}, people you add can view your plan. Upgrade to let someone change it with you.',
  'upgrade.body.editors.one': '{tier} includes 1 editor per plan, and this plan has one. Upgrade for more, or make someone a viewer first.',
  'upgrade.body.editors.some': '{tier} includes {limit} editors per plan, and this plan has them all. Upgrade for more, or make someone a viewer first.',
  'upgrade.title.plan_read_only': 'This plan is read-only',
  'upgrade.body.plan_read_only': 'This plan is read-only on {tier}. Upgrade to edit it again — nothing has been deleted.',
  'upgrade.title.editor_seat': 'Your changes are paused',
  'upgrade.body.editor_seat': 'You can view this plan. Its owner’s plan includes fewer editors, so your changes are paused until they upgrade.',
  'upgrade.title.lapsed': 'Your subscription has ended',
  'upgrade.body.lapsed': 'Your plans are read-only until you subscribe again. Nothing has been deleted, and nothing was saved.',
  'upgrade.title.branding': 'Branded share pages are part of Coach',
  'upgrade.body.branding': 'Coach puts your name and colour on the plans you share with clients.',

  'pricing.eyebrow': 'Plans and pricing',
  'pricing.title': 'Map My Money Plus',
  'pricing.subtitle': 'Your money, mapped forward: forecasts, what-ifs and goals on the web and with your own AI assistant.',
  'pricing.subtitle.lapsed': 'Your plans are all still here. Resubscribe to edit them again.',
  'pricing.reason.plans': 'You’ve reached the number of plans your current plan includes.',
  'pricing.reason.shares': 'You’ve reached the number of live links your current plan includes.',
  'pricing.reason.share_expiry': 'Choosing how long a link lasts is part of Plus.',
  'pricing.reason.plan_people': 'That plan is shared with as many people as your current plan allows.',
  'pricing.reason.editors': 'Adding editors to a plan is part of Plus.',
  'pricing.reason.plan_read_only': 'Some of your plans are read-only because you have more than your plan includes. Nothing has been deleted.',
  'pricing.reason.editor_seat': 'A plan’s owner needs more editor seats before your changes can be saved.',
  'pricing.reason.lapsed': 'Your subscription has ended, so your plans are read-only. Resubscribe to edit them again; nothing has been deleted.',
  'pricing.reason.branding': 'Branded share pages are part of Coach.',
  'pricing.interval': 'Billing period',
  'pricing.monthly': 'Monthly',
  'pricing.annual': 'Annual',
  'pricing.save': 'Save {pct}%',
  'pricing.perMonth': '/month',
  'pricing.perYear': '/year',
  'pricing.equivalent': 'Works out at {amount} a month',
  'pricing.monthlyNote': 'Billed every month. Cancel any time.',
  'pricing.plus.tagline': 'Everything Map My Money does, for one simple price.',
  'pricing.coach.tagline': 'For coaches and planners working with clients.',
  'pricing.coach.billing': 'Billed yearly',
  'pricing.current': 'Your plan',
  'pricing.currentBadge': 'Current',
  'pricing.included': 'Included in Coach',
  'pricing.start': 'Start your plan',
  'pricing.resubscribe': 'Resubscribe',
  'pricing.chooseCoach': 'Choose Coach',
  'pricing.coachApply': 'Apply for Coach',
  'pricing.coachByApplication': 'By application. Tell us a little about your clients and we’ll set you up.',
  'pricing.redirecting': 'Opening checkout…',
  'pricing.unavailable': 'Upgrades open soon. Everything you have keeps working in the meantime.',
  'pricing.error': 'Checkout didn’t open. Check your connection and try again.',
  'pricing.feature.plans': '{n} plans of your own',
  'pricing.feature.plansAny': 'As many plans as you like',
  'pricing.feature.shares': '{n} live share links',
  'pricing.feature.sharesAny': 'As many live links as you like',
  'pricing.feature.people': 'Share each plan with {n} people',
  'pricing.feature.peopleAny': 'Share each plan with anyone',
  'pricing.feature.viewersOnly': 'People you add can view',
  'pricing.feature.editorsOne': '1 editor per plan',
  'pricing.feature.editors': '{n} editors per plan',
  'pricing.feature.editorsAny': 'Editors on every plan',
  'pricing.feature.expiry': 'Choose how long each link lasts',
  'pricing.feature.expiryStandard': 'Links use the standard expiry',
  'pricing.feature.branding': 'Your name and colour on shared pages',
  'pricing.feature.clients': '{n} clients included, then {price}/client/month, billed yearly',
  'pricing.always.title': 'What’s always free',
  'pricing.always.open': 'Opening and playing with plans people share with you',
  'pricing.always.try': 'The playground: try everything with a sample household',
  'pricing.always.tryLink': 'Open the playground',
  'pricing.downgrade.title': 'If you stop, nothing is deleted',
  'pricing.downgrade.body': 'Your plans stay, read-only, and you can still sign in, export them or subscribe again any time. Links you’ve shared keep working until they expire.',
  'pricing.vat': 'Prices include VAT where it applies. Cancel any time from Manage billing; you keep your plan until the end of the period you’ve paid for.',
  'pricing.early': 'Early access: no limits apply yet, so you have room on every plan today.',

  'billingReturn.success.title': 'You’re on {tier}',
  'billingReturn.success.body': 'Thank you for supporting Map My Money. Your new limits apply now, on the web and with your assistant.',
  'billingReturn.success.home': 'Back to your plan',
  'billingReturn.success.billing': 'See billing',
  'billingReturn.confirming.title': 'Confirming your payment…',
  'billingReturn.confirming.body': 'This usually takes a few seconds.',
  'billingReturn.slow.title': 'Still confirming your payment…',
  'billingReturn.slow.body': 'This can take a minute. You can leave this page; your plan will update by itself.',
  'billingReturn.slow.retry': 'Check again',
  'billingReturn.cancelled.title': 'No changes made',
  'billingReturn.cancelled.body': 'You left checkout before paying, so nothing was charged and your plan is just as it was.',
  'billingReturn.cancelled.back': 'Back to plans',

  'plans.locked': 'Read-only',
  'plans.locked.planLimit': 'This plan is read-only on {tier}. Upgrade to edit it again — nothing has been deleted.',
  'plans.locked.lapsed': 'Your subscription has ended, so this plan is read-only. Nothing has been deleted.',
  'plans.locked.lapsedMember': 'This plan is read-only because its owner’s subscription has ended.',
  'plans.locked.editorSeat': 'You can view this plan. Its owner’s plan includes fewer editors, so your changes are paused until they upgrade.',
  'plans.capNoLimit': '{used} plans',
  'plans.switcher.more': 'Room for more plans',
  'shareNew.expiryStandard': 'Standard: {n} days',
  'shareNew.expiryPlus': 'Choosing how long a link lasts is part of Plus.',
  'smap.brand.sharedBy': 'Shared by {name}',
  'pricing.feature.core': 'Your plan, forecasts, what-ifs and goals',
  'pricing.feature.assistant': 'Ask Claude or ChatGPT about your plan',
  'pricing.signedOut.signIn': 'Already have an account? Sign in',
  'welcome.confirming.title': 'Confirming your payment…',
  'welcome.confirming.body': 'This usually takes a few seconds.',
  'welcome.slow.title': 'Still confirming your payment…',
  'welcome.slow.body': 'Stripe can take a minute. Keep this page open and check again shortly; you won’t be charged twice.',
  'welcome.slow.retry': 'Check again',
  'welcome.error': 'We couldn’t check your payment just now. Check your connection and try again.',
  'welcome.ready.eyebrow': 'Payment confirmed',
  'welcome.ready.title': 'Set a password to finish',
  'welcome.ready.body': 'Your Plus subscription is active. Choose a password for {email} and you’re in.',
  'welcome.ready.submit': 'Set password and continue',
  'welcome.ready.pending': 'Setting up your account…',
  'welcome.ready.note': 'You’ll sign in with {email} and this password.',
  'welcome.existing.title': 'You already have an account',
  'welcome.existing.body': 'Your subscription is on the account for {email}. Sign in to carry on.',
  'welcome.existing.cta': 'Sign in',
  'welcome.expired.title': 'This link can’t be used any more',
  'welcome.expired.body': 'A set-up link works once, for a day. If you’ve paid and can’t sign in, write to support@mapmymoney.app and we’ll get you set up.',
  'welcome.expired.cta': 'Go to sign in',
  'welcome.expired.support': 'Email support',
  'welcome.note.title': 'Welcome to Map My Money',
  'welcome.note.body': 'Your account is ready. Add your income and regular spending, and your forecast fills in as you go.',
  'welcome.note.dismiss': 'Dismiss',
  'signin.subtitlePaid': 'Welcome back. Sign in to your plan.',
  'signin.newcomer': 'New to Map My Money?',
  'signin.start': 'Start your plan',
  'signin.try': 'Try it first',
  'signin.paidNoPassword': 'Paid but haven’t set a password?',
  'signin.paidNoPasswordBody': 'Use the page you saw after paying to set one. Closed it? Write to support@mapmymoney.app from the email you paid with and we’ll set it up.',

  'startEntry.eyebrow': 'Start',
  'startEntry.title': 'See where your money’s heading',
  'startEntry.body': 'About a minute. No bank login, ever. You’ll see everything before it’s added.',
  'startEntry.pick': 'Pick what applies',
  'startEntry.pickBody': 'Salary, rent, car loan, pension… with typical amounts to change',
  'startEntry.drop': 'Drop a bank export',
  'startEntry.dropBody': 'A CSV file or pasted rows, read in your browser. The file is never uploaded.',
  'startEntry.persona': 'Start from someone like you',
  'startEntry.personaBody': '10 example households, UK and US. Swap their numbers for yours',
  'startEntry.question': 'Just one question',
  'startEntry.questionBody': 'Three numbers and a date',
  'startEntry.assistant': 'Or tell your own Claude or ChatGPT',
  'startEntry.self': 'Or add things yourself',
  'startEntry.pricing.title': 'See your own map first',
  'startEntry.pricing.body': 'About a minute, no bank login and nothing to pay. Keep it if it’s useful.',
  'startEntry.pricing.cta': 'Start my map',
  'startEntry.try': 'Use my own numbers',

  'finish.saved.title': 'Your map is saved',
  'finish.saved.body': 'Same numbers you just saw. Your first check-in is after payday on {date}.',
  'finish.saved.bodyNoPay': 'Same numbers you just saw.',
  'finish.title': 'Finish your map',
  'finish.count': '{done} of {total}',
  'finish.payments': 'Regular payments',
  'finish.balances': 'Today’s balances',
  'finish.goal': 'A goal',
  'finish.pension': 'Your pension and savings balances',
  'finish.examples': 'Swap {n} example amounts for yours',
  'finish.examplesOne': 'Swap 1 example amount for yours',
  'finish.checks': 'Check {n} “check” amounts',
  'finish.checksOne': 'Check 1 “check” amount',
  'finish.invite': 'Invite your partner',
  'finish.assistant': 'Keep it up to date from Claude or ChatGPT',
  'finish.add': 'Add',
  'finish.review': 'Review',
  'finish.connect': 'Connect',
  'finish.checkIn': 'Check in',
  'finish.dismiss': 'Hide this list',
  'finish.draft.title': 'Your map from Start is still in this browser',
  'finish.draft.body': '{n} items you picked before signing in. Nothing is added until you say.',
  'finish.draft.cta': 'Add them to this plan',
  'finish.draft.discard': 'Throw it away',
  'plan.mark.example': 'example',
  'plan.mark.check': 'check',
  'plan.mark.exampleLabel': 'An example amount from Start: change it to yours',
  'plan.mark.checkLabel': 'From Start: worth a check',
} as const;

export type MessageKey = keyof typeof en;
